import { MAX_ENTITY_REF_IDS } from '@orn/global/assistant/inlineMarkup.js';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';

/**
 * The alerts a transcript's cards name, deduped and in a stable order.
 *
 * Deduped because a transcript holds one card per expand, not per alert:
 * re-clicking an alert row deliberately re-inserts it, so live transcripts and
 * rehydrated history alike can carry several cards for the same alert. The
 * dedupe is what keeps the work-item read's query variables one-per-alert and
 * the {@link MAX_ENTITY_REF_IDS} cap counting alerts rather than clicks.
 * Sorted so a re-render that reorders nothing produces identical query
 * variables and hits the Apollo cache instead of refetching.
 *
 * Capped at {@link MAX_ENTITY_REF_IDS}, which is the page size the read clamps
 * to: asking for more returns one page anyway, so the excess is dropped here
 * where it is visible rather than silently truncated server-side. A transcript
 * with that many distinct cards is far past any real session.
 */
export function collectAlertCardIds(messages: readonly IConversationMessage[]): string[] {
  const ids = new Set<string>();
  for (const message of messages) {
    if (message.kind === 'alert-card' && message.alertId !== undefined) {
      ids.add(message.alertId);
    }
  }
  return [...ids].toSorted().slice(0, MAX_ENTITY_REF_IDS);
}

/**
 * The alert the transcript is holding open: the newest card's, until a later
 * row moves the conversation on.
 *
 * Read off the transcript rather than set when an expand is sent, so a
 * rejected or discarded expand — which never inserts a card, or has it
 * retracted — never names an alert. A card carried over from an earlier
 * thread is history, never held open. A user row retires the card unless it
 * is grounded in that same alert (a chip on the reply the card opened), and a
 * welcome row retires it as the start of a new sitting.
 */
export function findHeldOpenAlertId(messages: readonly IConversationMessage[]): string | null {
  const cardIndex = messages.findLastIndex((message) => message.kind === 'alert-card' && message.alertId !== undefined);
  const card = messages[cardIndex];
  const alertId = card?.alertId;
  if (alertId === undefined || card.isPriorThread === true) {
    return null;
  }

  const movedOn = messages
    .slice(cardIndex + 1)
    .some((message) => message.kind === 'welcome' || (message.role === 'user' && message.sourceAlertId !== alertId));
  return movedOn ? null : alertId;
}
